import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Stethoscope, Play, Loader2, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Info,
} from 'lucide-react';

const OUTCOME_STYLE = {
  healed: { c: 'text-emerald-700 bg-emerald-50 border-emerald-200', I: CheckCircle2 },
  would_heal_flag_off: { c: 'text-blue-700 bg-blue-50 border-blue-200', I: Info },
  dry_run: { c: 'text-blue-700 bg-blue-50 border-blue-200', I: Info },
  manual_review: { c: 'text-amber-700 bg-amber-50 border-amber-200', I: AlertTriangle },
  skipped_already_valid: { c: 'text-muted-foreground bg-muted border-border', I: CheckCircle2 },
  skipped_attempt_cap: { c: 'text-muted-foreground bg-muted border-border', I: XCircle },
  skipped_unchanged_inputs: { c: 'text-muted-foreground bg-muted border-border', I: XCircle },
  error: { c: 'text-rose-700 bg-rose-50 border-rose-200', I: XCircle },
};

function Stat({ label, value, accent = 'text-foreground' }) {
  return (
    <div className="border rounded-lg p-3 bg-card" data-testid={`healer-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-black tracking-tight ${accent}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</div>
    </div>
  );
}

export default function AiHealerPage({ accounts = [] }) {
  const [accountId, setAccountId] = useState('');
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [limit, setLimit] = useState(10);
  const [results, setResults] = useState(null);

  const acctName = (a) => a.name || a.company_name || a.org_name || a.id;

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [cfg, st] = await Promise.all([
        api.get('/ai-healer/config', { params: { account_id: accountId } }),
        api.get('/ai-healer/stats', { params: { account_id: accountId } }),
      ]);
      setConfig(cfg.data);
      setStats(st.data);
    } catch (e) {
      toast.error('Failed to load AI Healer status');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async (dry) => {
    if (!accountId) { toast.error('Select an org first'); return; }
    setSweeping(true);
    setResults(null);
    try {
      const { data } = await api.post('/ai-healer/sweep', {
        account_id: accountId, limit: Number(limit), dry_run: dry,
      });
      setResults(data);
      toast.success(`Swept ${data.scanned} bill(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="ai-healer-cockpit">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-violet-600" />
          <h2 className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            AI Validation Healer
          </h2>
          {config?.model && (
            <Badge variant="outline" className="text-[10px]">{config.model}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="h-9 w-64 text-xs" data-testid="healer-account-select">
              <SelectValue placeholder="Select an org…" />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{acctName(a)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={load} disabled={!accountId || loading} data-testid="healer-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {!accountId && (
        <div className="border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="healer-no-account">
          Select an org to review its AI Healer backlog and run dry-runs.
        </div>
      )}

      {accountId && (
        <>
          {/* Enablement banner */}
          <div className={`border rounded-lg px-4 py-3 flex items-center gap-3 text-sm ${config?.enabled ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="healer-enabled-banner">
            {config?.enabled ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            <span>
              Auto-apply is <strong>{config?.enabled ? 'ON' : 'OFF'}</strong> for this org.
              {!config?.enabled && ' Heals run in dry-run/observe mode only — enable in SuperAdmin → Edit Org → Workflow Config → AI Validation Healer.'}
            </span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="healer-stats">
            <Stat label="Needs Review" value={stats?.needs_review ?? '—'} accent="text-rose-600" />
            <Stat label="Healable Backlog" value={stats?.healable_backlog ?? '—'} accent="text-violet-600" />
            <Stat label="Healed" value={stats?.by_heal_status?.healed ?? 0} accent="text-emerald-600" />
            <Stat label="Manual Review" value={stats?.by_heal_status?.manual_review ?? 0} accent="text-amber-600" />
          </div>

          {/* Sweep controls */}
          <div className="border rounded-lg p-4 bg-card space-y-3" data-testid="healer-sweep-controls">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium">Run on backlog:</span>
              <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                <SelectTrigger className="h-9 w-28 text-xs" data-testid="healer-limit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((n) => <SelectItem key={n} value={String(n)}>{n} bills</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-9" onClick={() => runSweep(true)} disabled={sweeping} data-testid="healer-dryrun-btn">
                {sweeping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                Dry-Run Sweep
              </Button>
              <Button size="sm" className="h-9 bg-violet-600 hover:bg-violet-700" onClick={() => runSweep(false)} disabled={sweeping || !config?.enabled} data-testid="healer-apply-btn">
                <Stethoscope className="h-4 w-4 mr-2" /> Apply Heals
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Dry-Run diagnoses + previews without writing. &quot;Apply Heals&quot; writes only the heals that pass the improve-or-discard gate, and is disabled unless auto-apply is ON for this org.
            </p>
          </div>

          {/* Results */}
          {results && (
            <div className="border rounded-lg bg-card" data-testid="healer-results">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  Sweep results · {results.scanned} scanned {results.dry_run && <Badge variant="outline" className="ml-2 text-[10px]">dry-run</Badge>}
                </h3>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(results.counts || {}).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                  ))}
                </div>
              </div>
              <div className="divide-y max-h-[420px] overflow-auto" data-testid="healer-results-list">
                {(results.results || []).length === 0 && (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    No healable bills in the backlog for this org.
                  </div>
                )}
                {(results.results || []).map((r, i) => {
                  const s = OUTCOME_STYLE[r.outcome] || OUTCOME_STYLE.manual_review;
                  const I = s.I;
                  return (
                    <div key={r.document_id || i} className="px-4 py-2.5 flex items-start gap-3" data-testid={`healer-result-${i}`}>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium whitespace-nowrap ${s.c}`}>
                        <I className="h-3 w-3" /> {r.outcome}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-mono text-muted-foreground truncate">{r.document_id}</div>
                        {r.reason && <div className="text-xs text-foreground mt-0.5">{r.reason}</div>}
                      </div>
                      {(r.would_accept ?? r.accepted) != null && (
                        <Badge variant="outline" className={`text-[10px] ${(r.would_accept ?? r.accepted) ? 'text-emerald-700 border-emerald-300' : 'text-muted-foreground'}`}>
                          {(r.would_accept ?? r.accepted) ? 'would heal' : 'declined'}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
